import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  Pressable,
  ActivityIndicator,
  Platform,
  Alert,
  Linking,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { searchUsers, getUserProfile, promoteUserToHost, promoteUserToPartner } from '../../services/user-service';
import { getHostPlans, getPartnerPlans} from '../../services/user-plans-service';
import { HostPlanAttributes } from '../../models/host-plan-model';
import { PartnerPlanAttributes } from '../../models/partner-plan-model';


/**
 * ---- Types (align to your backend payloads if you already have models) ----
 * Kept flexible but strongly typed for the attributes you asked to display.
 */
type UserSummary = {
  uuid: string;
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  city?: string;
  createdAt?: string | Date;
};

type HostPlan = {
  planId: string;
  name: string;
  price: number; // cents or dollars? Assuming dollars here—adjust formatCurrency if needed
  billing_interval: 'day' | 'week' | 'month' | 'year';
  billing_date?: string | null; // ISO date or format; display as datetime
  stripe_price_id?: string | null;
  createdAt?: string;
};

type PartnerPlan = {
  planId: string;
  name: string;
  contract_link?: string | null;
  collection_interval: 'day' | 'week' | 'month' | 'year';
  payout_date?: string | null; // ISO date or format
  createdAt?: string;
};

type SearchBy = 'username' | 'email' | 'last_name' | 'city';

const SEARCH_LABEL: Record<SearchBy, string> = {
    username: 'Username',
    email: 'Email',
    last_name: 'Last name',
    city: 'City',
};
  

const MIN_CHARS = 4;
const MAX_RESULTS = 5;
const DEBOUNCE_MS = 350;

const UserManagement: React.FC = () => {
  const [searchBy, setSearchBy] = useState<SearchBy>('username');
  const [showSearchByMenu, setShowSearchByMenu] = useState(false);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<UserSummary[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Selection
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null);

  // Plans
  const [hostPlans, setHostPlans] = useState<HostPlanAttributes[]>([]);
  const [partnerPlans, setPartnerPlans] = useState<PartnerPlanAttributes[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);

  // Selected plans
  const [selectedHostPlanId, setSelectedHostPlanId] = useState<number | null>(null);
  const [selectedPartnerPlanId, setSelectedPartnerPlanId] = useState<number | null>(null);

  // Action loading flags
  const [promotingHost, setPromotingHost] = useState(false);
  const [promotingPartner, setPromotingPartner] = useState(false);

  // Race-condition guards for search
  const searchTokenRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Debounced search
  useEffect(() => {
    if (query.trim().length < MIN_CHARS) {
      setResults([]);
      setShowDropdown(false);
      setSearching(false);
      setSearchError(null);
      return;
    }
  
    const token = ++searchTokenRef.current;
    setSearching(true);
    setSearchError(null);
  
    const timer = setTimeout(async () => {
      try {
        const payload = await searchUsers({ [searchBy]: query.trim() });
        if (!mountedRef.current || token !== searchTokenRef.current) return;
  
        const list = Array.isArray(payload) ? payload : [];
        const top = list.slice(0, MAX_RESULTS);
        setResults(top);
        setShowDropdown(true);
      } catch (e: any) {
        if (!mountedRef.current || token !== searchTokenRef.current) return;
        setSearchError(e?.message || 'Search failed');
        setResults([]);
        setShowDropdown(true);
      } finally {
        if (mountedRef.current && token === searchTokenRef.current) {
          setSearching(false);
        }
      }
    }, DEBOUNCE_MS);
  
    return () => clearTimeout(timer);
  }, [query, searchBy]);  

  // Load plans once (or when user selected, if you want to defer)
  useEffect(() => {
    let cancel = false;
    const loadPlans = async () => {
      setPlansLoading(true);
      setPlansError(null);
      try {
        const [hosts, partners] = await Promise.all([getHostPlans(), getPartnerPlans()]);
        if (cancel || !mountedRef.current) return;
        setHostPlans(Array.isArray(hosts) ? hosts : []);
        setPartnerPlans(Array.isArray(partners) ? partners : []);
      } catch (e: any) {
        if (cancel || !mountedRef.current) return;
        setPlansError(e?.message || 'Failed to load plans');
      } finally {
        if (!cancel && mountedRef.current) {
          setPlansLoading(false);
        }
      }
    };
    loadPlans();
    return () => {
      cancel = true;
    };
  }, []);

  // Helpers
  const safeAlert = useCallback((title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(`${title}: ${message}`);
    } else {
      Alert.alert(title, message);
    }
  }, []);

  const formatDateTime = (d?: string | Date | number | null) => {
    if (d == null) return '—';
  
    let date: Date;
  
    if (d instanceof Date) {
      date = d;
    } else if (typeof d === 'number') {
      // treat as ms since epoch
      date = new Date(d);
    } else if (typeof d === 'string') {
      // ISO string (or anything Date can parse)
      date = new Date(d);
    } else {
      return '—';
    }
  
    if (isNaN(date.getTime())) return '—';
  
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };
  

  const formatCurrency = useCallback((amt?: number | null) => {
    if (amt == null) return '—';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(amt);
    } catch {
      return `$${amt}`;
    }
  }, []);

  // Handlers
  const handleSelectUser = useCallback((u: UserSummary) => {
    setSelectedUser(u);
    setQuery(u.username);
    setShowDropdown(false);
    // Reset plan picks for clarity when user changes
    setSelectedHostPlanId(null);
    setSelectedPartnerPlanId(null);
  }, []);

  const canPromoteToHost = useMemo(
    () => !!selectedUser && !!selectedHostPlanId && !promotingHost,
    [selectedUser, selectedHostPlanId, promotingHost]
  );

  const canPromoteToPartner = useMemo(
    () => !!selectedUser && !!selectedPartnerPlanId && !promotingPartner,
    [selectedUser, selectedPartnerPlanId, promotingPartner]
  );

  const selectedHostPlan = useMemo(
    () => hostPlans.find(p => p.planId === selectedHostPlanId) || null,
    [hostPlans, selectedHostPlanId]
  );

  const selectedPartnerPlan = useMemo(
    () => partnerPlans.find(p => p.planId === selectedPartnerPlanId) || null,
    [partnerPlans, selectedPartnerPlanId]
  );

  const onPromoteHost = useCallback(async () => {
    if (!selectedUser || !selectedHostPlanId) return;
    setPromotingHost(true);
    try {
      await promoteUserToHost(selectedUser.uuid, selectedHostPlanId);
      safeAlert('Success', `${selectedUser.username} promoted to Host`);
    } catch (e: any) {
      safeAlert('Error', e?.message || 'Failed to promote user to Host');
    } finally {
      setPromotingHost(false);
    }
  }, [selectedUser, selectedHostPlanId, safeAlert]);

  const onPromotePartner = useCallback(async () => {
    if (!selectedUser || !selectedPartnerPlanId) return;
    setPromotingPartner(true);
    try {
      await promoteUserToPartner(selectedUser.uuid, selectedPartnerPlanId);
      safeAlert('Success', `${selectedUser.username} promoted to Partner`);
    } catch (e: any) {
      safeAlert('Error', e?.message || 'Failed to promote user to Partner');
    } finally {
      setPromotingPartner(false);
    }
  }, [selectedUser, selectedPartnerPlanId, safeAlert]);

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
    >
      {/* Search Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Find a User</Text>
        <Text style={styles.sectionDescription}>
            Choose a field, then type at least {MIN_CHARS} characters.
        </Text>

        {/* Field selector + input */}
<View style={styles.searchRow}>
  {/* Field selector toggle */}
    <Pressable
        style={({ pressed }) => [
        styles.searchBySelector,
        pressed && { backgroundColor: '#2A2A2A' },
        ]}
        onPress={() => setShowSearchByMenu((v) => !v)}
        accessibilityRole="button"
        accessibilityLabel="Change search field"
    >
        <MaterialIcons name="tune" size={18} color="#95F5AA" />
        <Text style={styles.searchByText}>{SEARCH_LABEL[searchBy]}</Text>
        <MaterialIcons
        name={showSearchByMenu ? 'expand-less' : 'expand-more'}
        size={20}
        color="#fff"
        />
    </Pressable>

    {/* Query input */}
    <MaterialIcons
        name="search"
        size={20}
        color={query.length >= MIN_CHARS ? '#95F5AA' : '#666'}
        style={{ marginHorizontal: 4 }}
    />
    <TextInput
        style={styles.input}
        placeholder={`Search ${SEARCH_LABEL[searchBy]}...`}
        placeholderTextColor="#666"
        value={query}
        onChangeText={setQuery}
        autoCapitalize="none"
        autoCorrect={false}
    />
    {searching && <ActivityIndicator size="small" color="#00D9FF" />}
    </View>

    {/* Field dropdown (under row) */}
    {showSearchByMenu && (
        <View style={styles.searchByDropdown}>
            {(['username', 'email', 'last_name', 'city'] as SearchBy[]).map((opt) => (
            <Pressable
                key={opt}
                style={({ pressed }) => [
                styles.searchByItem,
                pressed && styles.searchByItemPressed,
                searchBy === opt && styles.searchByItemSelected,
                ]}
                onPress={() => {
                setSearchBy(opt);
                setShowSearchByMenu(false);
                // keep query text; user may just be switching fields
                // also close results list to avoid confusion
                setResults([]);
                setShowDropdown(false);
                }}
            >
                <Text style={styles.searchByItemText}>{SEARCH_LABEL[opt]}</Text>
                {searchBy === opt && (
                <MaterialIcons name="check" size={18} color="#3FC67D" />
                )}
            </Pressable>
            ))}
        </View>
    )}


        {showDropdown && (
          <View style={styles.dropdown}>
            {searchError ? (
              <Text style={styles.noResultsText}>{searchError}</Text>
            ) : results.length === 0 ? (
              <Text style={styles.noResultsText}>No users found</Text>
            ) : (
              results.map((u) => (
                <Pressable
                  key={u.uuid}
                  style={({ pressed }) => [
                    styles.dropdownItem,
                    pressed && styles.dropdownItemPressed,
                  ]}
                  onPress={() => handleSelectUser(u)}
                >
                  <MaterialIcons name="person" size={18} color="#95F5AA" />
                  <View style={{ flex: 1 }}>
                    <Text style={styles.dropdownItemTitle} numberOfLines={1}>
                      {u.username} {u.first_name || u.last_name ? `• ${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : ''}
                    </Text>
                    <Text style={styles.dropdownItemSub} numberOfLines={1}>
                      {u.email ?? '—'} {u.city ? `• ${u.city}` : ''}
                    </Text>
                  </View>
                </Pressable>
              ))
            )}
          </View>
        )}

        {/* Selected user summary */}
        {selectedUser && (
          <View style={styles.selectionCard}>
            <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
              <MaterialIcons name="verified-user" size={22} color="#95F5AA" />
              <Text style={styles.selectionTitle}>Selected User</Text>
            </View>
            <View style={styles.selectionRow}>
              <Text style={styles.selectionLabel}>Username</Text>
              <Text style={styles.selectionValue}>{selectedUser.username}</Text>
            </View>
            {!!selectedUser.email && (
              <View style={styles.selectionRow}>
                <Text style={styles.selectionLabel}>Email</Text>
                <Text style={styles.selectionValue}>{selectedUser.email}</Text>
              </View>
            )}
            {(selectedUser.first_name || selectedUser.last_name) && (
              <View style={styles.selectionRow}>
                <Text style={styles.selectionLabel}>Name</Text>
                <Text style={styles.selectionValue}>
                  {(selectedUser.first_name ?? '') + ' ' + (selectedUser.last_name ?? '')}
                </Text>
              </View>
            )}
            {!!selectedUser.city && (
              <View style={styles.selectionRow}>
                <Text style={styles.selectionLabel}>City</Text>
                <Text style={styles.selectionValue}>{selectedUser.city}</Text>
              </View>
            )}
            <Pressable
              onPress={() => {
                setSelectedUser(null);
                setQuery('');
                setResults([]);
                setShowDropdown(false);
                setSelectedHostPlanId(null);
                setSelectedPartnerPlanId(null);
              }}
              style={({ pressed }) => [styles.clearButton, pressed && styles.clearButtonPressed]}
            >
              <MaterialIcons name="close" size={18} color="white" />
              <Text style={styles.clearButtonText}>Clear Selection</Text>
            </Pressable>
          </View>
        )}
      </View>

      {/* Plans Section */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <View>
            <Text style={styles.sectionTitle}>Assign Plans</Text>
            <Text style={styles.sectionDescription}>
              Choose a Host or Partner plan for the selected user.
            </Text>
          </View>
          {plansLoading ? (
            <ActivityIndicator size="small" color="#00D9FF" />
          ) : (
            <Pressable
              style={styles.refreshButton}
              onPress={async () => {
                setPlansLoading(true);
                setPlansError(null);
                try {
                  const [hosts, partners] = await Promise.all([getHostPlans(), getPartnerPlans()]);
                  if (!mountedRef.current) return;
                  setHostPlans(Array.isArray(hosts) ? hosts : []);
                  setPartnerPlans(Array.isArray(partners) ? partners : []);
                } catch (e: any) {
                  setPlansError(e?.message || 'Failed to refresh plans');
                } finally {
                  if (mountedRef.current) setPlansLoading(false);
                }
              }}
              disabled={plansLoading}
            >
              <MaterialIcons name="refresh" size={22} color="#95F5AA" />
            </Pressable>
          )}
        </View>

        {plansError && <Text style={styles.errorText}>{plansError}</Text>}

        {/* Host Plans */}
        <View style={styles.subSection}>
          <View style={styles.subHeader}>
            <MaterialIcons name="home-work" size={20} color="#95F5AA" />
            <Text style={styles.subTitle}>Host Plans</Text>
          </View>

          {hostPlans.length === 0 && !plansLoading ? (
            <Text style={styles.noResultsText}>No host plans available</Text>
          ) : (
            <View style={styles.radioList}>
              {hostPlans.map((p) => {
                const selected = selectedHostPlanId === p.planId;
                return (
                  <Pressable
                    key={p.planId}
                    style={({ pressed }) => [
                      styles.radioItem,
                      selected && styles.radioItemSelected,
                      pressed && styles.radioItemPressed,
                    ]}
                    onPress={() => setSelectedHostPlanId(p.planId)}
                  >
                    <MaterialIcons
                      name={selected ? 'radio-button-checked' : 'radio-button-unchecked'}
                      size={20}
                      color={selected ? '#3FC67D' : '#888'}
                    />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.radioItemTitle}>{p.name}</Text>
                      <Text style={styles.radioItemSub}>
                        {formatCurrency(p.price)} • {p.billing_interval}
                      </Text>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          )}

          {/* Host Plan Details */}
          {selectedHostPlan && (
            <View style={styles.detailCard}>
              <Text style={styles.detailTitle}>Host Plan Details</Text>
              <DetailRow label="Name" value={selectedHostPlan.name} />
              <DetailRow label="Price" value={formatCurrency(selectedHostPlan.price)} />
              <DetailRow label="Billing Interval" value={selectedHostPlan.billing_interval} />
              <DetailRow label="Billing Date" value={selectedHostPlan.billing_date.toString()} />
              <DetailRow label="Stripe Price ID" value={selectedHostPlan.stripe_price_id ?? '—'} />
              <DetailRow label="Created" value={formatDateTime(selectedHostPlan.createdAt)} />
              <Pressable
                style={({ pressed }) => [
                  styles.primaryButton,
                  (!canPromoteToHost || pressed) && styles.primaryButtonPressed,
                  !canPromoteToHost && styles.primaryButtonDisabled,
                ]}
                disabled={!canPromoteToHost}
                onPress={onPromoteHost}
              >
                {promotingHost ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <>
                    <MaterialIcons name="upgrade" size={20} color="white" />
                    <Text style={styles.primaryButtonText}>Promote User To Host</Text>
                  </>
                )}
              </Pressable>
            </View>
          )}
        </View>

        {/* Divider */}
        <View style={styles.divider} />

        {/* Partner Plans */}
        <View style={styles.subSection}>
          <View style={styles.subHeader}>
            <MaterialIcons name="handshake" size={20} color="#95F5AA" />
            <Text style={styles.subTitle}>Partner Plans</Text>
          </View>

          {partnerPlans.length === 0 && !plansLoading ? (
            <Text style={styles.noResultsText}>No partner plans available</Text>
          ) : (
            <View style={styles.radioList}>
              {partnerPlans.map((p) => {
                const selected = selectedPartnerPlanId === p.planId;
                return (
                  <Pressable
                    key={p.planId}
                    style={({ pressed }) => [
                      styles.radioItem,
                      selected && styles.radioItemSelected,
                      pressed && styles.radioItemPressed,
                    ]}
                    onPress={() => setSelectedPartnerPlanId(p.planId ?? null)}
                  >
                    <MaterialIcons
                      name={selected ? 'radio-button-checked' : 'radio-button-unchecked'}
                      size={20}
                      color={selected ? '#3FC67D' : '#888'}
                    />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.radioItemTitle}>{p.name}</Text>
                      <Text style={styles.radioItemSub}>
                        Collection: {p.collection_interval}
                      </Text>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          )}

          {/* Partner Plan Details */}
          {selectedPartnerPlan && (
            <View style={styles.detailCard}>
              <Text style={styles.detailTitle}>Partner Plan Details</Text>
              <DetailRow label="Name" value={selectedPartnerPlan.name} />
              <DetailRow label="Contract" value={
                selectedPartnerPlan.contract_link
                  ? 'Open Contract'
                  : '—'
              }
                trailingIcon={
                  selectedPartnerPlan.contract_link ? (
                    <Pressable
                      onPress={() => {
                        if (selectedPartnerPlan.contract_link) {
                          Linking.openURL(selectedPartnerPlan.contract_link).catch(() => {
                            safeAlert('Error', 'Unable to open contract link');
                          });
                        }
                      }}
                      style={styles.linkPill}
                    >
                      <MaterialIcons name="open-in-new" size={16} color="white" />
                      <Text style={styles.linkPillText}>Open</Text>
                    </Pressable>
                  ) : undefined
                }
              />
              <DetailRow label="Collection Interval" value={selectedPartnerPlan.collection_interval ?? '—'} />
              <DetailRow label="Payout Date" value={selectedPartnerPlan.payout_date?.toString() ?? '—'} />
              <DetailRow label="Created" value={formatDateTime(selectedPartnerPlan.createdAt ?? '—')} />
              <Pressable
                style={({ pressed }) => [
                  styles.primaryButton,
                  (!canPromoteToPartner || pressed) && styles.primaryButtonPressed,
                  !canPromoteToPartner && styles.primaryButtonDisabled,
                ]}
                disabled={!canPromoteToPartner}
                onPress={onPromotePartner}
              >
                {promotingPartner ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <>
                    <MaterialIcons name="upgrade" size={20} color="white" />
                    <Text style={styles.primaryButtonText}>Promote User To Partner</Text>
                  </>
                )}
              </Pressable>
            </View>
          )}
        </View>
      </View>

      {/* Info */}
      <View style={styles.infoSection}>
        <MaterialIcons name="info-outline" size={20} color="#00D9FF" />
        <Text style={styles.infoText}>
          Choose a user, pick a plan, then confirm promotion. Searches are debounced and limited to {MAX_RESULTS} results.
        </Text>
      </View>
    </ScrollView>
  );
};

const DetailRow: React.FC<{
  label: string;
  value: string;
  trailingIcon?: React.ReactNode;
}> = ({ label, value, trailingIcon }) => {
  return (
    <View style={styles.detailRow}>
      <Text style={styles.detailLabel}>{label}</Text>
      <View style={styles.detailValueWrap}>
        <Text style={styles.detailValue} numberOfLines={1}>
          {value}
        </Text>
        {trailingIcon}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#1E1E1E' },
  contentContainer: { padding: 16, gap: 20 },

  section: {
    backgroundColor: '#2A2A2A',
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: '#00D9FF33',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  sectionTitle: { fontSize: 18, fontWeight: '600', color: 'white', marginBottom: 6 },
  sectionDescription: { fontSize: 14, color: '#AAA', lineHeight: 20 },
  highlight: { color: '#95F5AA' },

  // Search
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#333',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#444',
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginTop: 12,
  },
  input: { flex: 1, color: 'white', fontSize: 16 },
  dropdown: {
    marginTop: 8,
    backgroundColor: '#333',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#444',
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#2A2A2A',
  },
  dropdownItemPressed: { backgroundColor: '#2A2A2A' },
  dropdownItemTitle: { fontSize: 15, color: 'white' },
  dropdownItemSub: { fontSize: 12, color: '#AAA', marginTop: 2 },
  noResultsText: { fontSize: 14, color: '#666', textAlign: 'center', padding: 14 },

  // Selection card
  selectionCard: {
    marginTop: 12,
    backgroundColor: '#1E1E1E',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#444',
    padding: 14,
    gap: 8,
  },
  selectionTitle: { color: 'white', fontSize: 16, fontWeight: '600' },
  selectionRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 12 },
  selectionLabel: { color: '#AAA', fontSize: 13, minWidth: 90 },
  selectionValue: { color: 'white', fontSize: 14, flex: 1, textAlign: 'right' },
  clearButton: {
    marginTop: 4,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    gap: 6,
    backgroundColor: '#555',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 6,
  },
  clearButtonPressed: { transform: [{ scale: 0.98 }], backgroundColor: '#444' },
  clearButtonText: { color: 'white', fontSize: 13, fontWeight: '600' },

  // Plan lists
  subSection: { marginTop: 8 },
  subHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  subTitle: { color: 'white', fontSize: 16, fontWeight: '600' },
  radioList: { gap: 8, marginTop: 6 },
  radioItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#333',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#444',
    padding: 12,
  },
  radioItemSelected: { borderColor: '#00D9FF' },
  radioItemPressed: { backgroundColor: '#2A2A2A' },
  radioItemTitle: { color: 'white', fontSize: 15, fontWeight: '600' },
  radioItemSub: { color: '#AAA', fontSize: 12, marginTop: 2 },

  // Details
  detailCard: {
    marginTop: 10,
    backgroundColor: '#1E1E1E',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#444',
    padding: 14,
    gap: 10,
  },
  detailTitle: { color: 'white', fontSize: 15, fontWeight: '700' },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    justifyContent: 'space-between',
  },
  detailLabel: { color: '#AAA', fontSize: 13, minWidth: 130 },
  detailValueWrap: { flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'flex-end' },
  detailValue: { color: 'white', fontSize: 14, textAlign: 'right', flexShrink: 1 },

  linkPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#00B8D9',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
  },
  linkPillText: { color: 'white', fontSize: 12, fontWeight: '700' },

  // Buttons
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'center',
    backgroundColor: '#3FC67D',
    padding: 14,
    borderRadius: 8,
    marginTop: 6,
  },
  primaryButtonPressed: { transform: [{ scale: 0.98 }] },
  primaryButtonDisabled: { backgroundColor: '#555', opacity: 0.6 },
  primaryButtonText: { color: 'white', fontSize: 15, fontWeight: '700' },

  divider: { height: 1, backgroundColor: '#00D9FF33', marginVertical: 12 },

  // Info & misc
  refreshButton: { padding: 6 },
  errorText: { color: '#FF8C00', marginTop: 4 },
  infoSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#2A2A2A',
    borderRadius: 12,
    padding: 16,
    gap: 12,
    borderWidth: 1,
    borderColor: '#00D9FF33',
  },
  infoText: { flex: 1, fontSize: 13, color: '#AAA', lineHeight: 18 },
  searchBySelector: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#333',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#444',
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  searchByText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  searchByDropdown: {
    marginTop: 8,
    backgroundColor: '#333',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#444',
    overflow: 'hidden',
  },
  searchByItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#2A2A2A',
  },
  searchByItemPressed: {
    backgroundColor: '#2A2A2A',
  },
  searchByItemSelected: {
    backgroundColor: '#2E2E2E',
  },
  searchByItemText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  
});

export default UserManagement;